"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Icon } from "@/components/ui/icon";
import { ENROLLMENT_STATUS_META } from "@/features/enrollment/lib/status-meta";
import type { EnrollmentStatus } from "@/features/enrollment/lib/types";
import { sequenceColor } from "@/features/sequences/lib/colors";
import type { PipelineCandidate } from "@/lib/api/types";
import type { PipelineCandidateSequenceInfo } from "@/lib/api/model";

/** Gap between pills (matches `gap-1.5`). */
const PILL_GAP_PX = 6;
/** Reserved room for the "+N" overflow badge when not all pills fit. */
const OVERFLOW_BADGE_PX = 34;

function SequencePill({ seq }: { seq: PipelineCandidateSequenceInfo }) {
  const meta = ENROLLMENT_STATUS_META[seq.status as EnrollmentStatus];
  const terminal = meta?.is_terminal ?? false;
  return (
    <span className={terminal ? "opacity-55" : undefined}>
      <Badge
        variant="linear"
        size="xs"
        className="max-w-36"
        style={{ "--gradient-color": sequenceColor(seq.sequence_id) } as React.CSSProperties}
        title={meta ? `${seq.name}: ${meta.label}` : seq.name}
      >
        <span className="truncate">{seq.name}</span>
      </Badge>
    </span>
  );
}

/**
 * Shows the pills that fit the current (user-resizable, persisted) column
 * width, collapsing the rest behind a "+N" badge. All data arrives with the
 * pipeline list response itself — nothing here fetches.
 */
export function SequencesCell({ candidate }: { candidate: PipelineCandidate }) {
  const rawSequences = candidate.sequences;
  const sequences = useMemo(() => rawSequences ?? [], [rawSequences]);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(sequences.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || sequences.length === 0) return;

    const compute = () => {
      const available = container.clientWidth;
      const widths = Array.from(measure.children).map(
        (el) => (el as HTMLElement).offsetWidth,
      );
      // How many pills fit as-is?
      let used = 0;
      let fitAll = 0;
      for (const w of widths) {
        const next = used + (fitAll > 0 ? PILL_GAP_PX : 0) + w;
        if (next > available) break;
        used = next;
        fitAll += 1;
      }
      if (fitAll >= widths.length) {
        setVisibleCount(widths.length);
        return;
      }
      // Not all fit — re-fit while reserving room for the "+N" badge,
      // always keeping at least one pill visible.
      used = 0;
      let fit = 0;
      for (const w of widths) {
        const next = used + (fit > 0 ? PILL_GAP_PX : 0) + w;
        if (next > available - OVERFLOW_BADGE_PX - PILL_GAP_PX) break;
        used = next;
        fit += 1;
      }
      setVisibleCount(Math.max(1, fit));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [sequences]);

  if (sequences.length === 0) {
    return (
      <span className="block truncate whitespace-nowrap text-sm text-muted-foreground">
        No Sequences
      </span>
    );
  }

  const visible = sequences.slice(0, visibleCount);
  const hiddenCount = sequences.length - visible.length;

  return (
    <div ref={containerRef} className="relative min-w-0 overflow-hidden">
      {/* Invisible measurement row: every pill at natural width. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center gap-1.5"
      >
        {sequences.map((seq) => (
          <SequencePill key={seq.sequence_id} seq={seq} />
        ))}
      </div>

      <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
        {visible.map((seq) => (
          <SequencePill key={seq.sequence_id} seq={seq} />
        ))}
        {hiddenCount > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 rounded-sm border border-border bg-contrast/5 px-1 py-0.5 text-xs font-medium text-contrast/75 transition-colors hover:bg-contrast/10"
                aria-label={`Show ${hiddenCount} more sequences`}
              >
                +{hiddenCount}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-64 p-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col gap-1.5">
                {sequences.map((seq) => {
                  const meta =
                    ENROLLMENT_STATUS_META[seq.status as EnrollmentStatus];
                  return (
                    <div
                      key={seq.sequence_id}
                      className="flex min-w-0 items-center justify-between gap-2"
                    >
                      <SequencePill seq={seq} />
                      {meta && (
                        <span
                          className={`flex shrink-0 items-center gap-1 text-xs ${meta.color}`}
                        >
                          <Icon name={meta.icon} size={12} />
                          {meta.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
